
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { TagInput } from '@/components/ui/tag-input';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormMessage, FormDescription, FormLabel } from '@/components/ui/form';
import { StickyNote, Tags } from 'lucide-react';

const noteSchema = z.object({
  content: z.string().min(1, "Note content is required").max(500, "Note must be less than 500 characters"),
  tags: z.array(z.string())
});

type NoteFormData = z.infer<typeof noteSchema>;

interface AddNoteFormProps {
  onAddNote: (content: string, tags?: string[]) => Promise<void>;
  availableTags: string[];
}

const AddNoteForm: React.FC<AddNoteFormProps> = ({ 
  onAddNote, 
  availableTags = []
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const maxLength = 500;
  
  const form = useForm<NoteFormData>({
    resolver: zodResolver(noteSchema),
    defaultValues: {
      content: '',
      tags: []
    }
  });

  const onSubmit = async (data: NoteFormData) => {
    try {
      await onAddNote(data.content, data.tags || []);
      form.reset();
      setIsOpen(false);
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !form.formState.isSubmitting) {
      form.handleSubmit(onSubmit)();
    }
  };

  const tagOptions = (availableTags || []).map(tag => ({ label: tag, value: tag }));
  const contentLength = form.watch('content')?.length || 0;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>Add Note</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px] p-0 elevation-floating backdrop-blur-md">
        <DialogHeader className="p-6 pb-4 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 border-b border-border/40">
          <div className="flex items-center gap-3">
            <StickyNote className="h-5 w-5 text-primary" />
            <DialogTitle className="text-2xl font-semibold tracking-tight">Add New Note</DialogTitle>
          </div>
          <DialogDescription className="text-muted-foreground/70">Keep important information about this lead</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} onKeyDown={handleKeyDown}>
            <div className="p-6 pt-5 space-y-6">
              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground/90">Note Content</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="What would you like to remember about this lead?"
                        {...field}
                        className="min-h-[140px] p-4 transition-all duration-200"
                        maxLength={maxLength}
                      />
                    </FormControl>
                    <div className="flex justify-between items-center">
                      <FormDescription>Capture key details and insights</FormDescription>
                      <span className="text-xs text-muted-foreground">{contentLength}/{maxLength}</span>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2 text-sm font-medium text-foreground/90">
                      <Tags className="h-4 w-4" />
                      Tags (optional)
                    </FormLabel>
                    <FormControl>
                      <TagInput
                        placeholder="Select or create tags..."
                        selected={field.value || []}
                        options={tagOptions}
                        onChange={field.onChange}
                        creatable
                      />
                    </FormControl>
                    <FormDescription>Press Enter to add tags</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter className="border-t border-border/40 px-6 py-4 bg-muted/20 flex justify-between items-center">
              <span className="text-xs text-muted-foreground">⌘+Enter to save</span>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="gradient" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting ? "Adding..." : "Add Note"}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddNoteForm;
